import Fuse from "fuse.js";
import { createSelector } from "reselect";
import { EMPTY_ARRAY } from "lib";
import {
  SideLoadedMatchedField,
  PIC_DELIMITER,
} from "constants/sideLoadedBusinesses";
import { getSideLoadedBusinesses } from "./root";
import { selectSideLoadedDataSetLabelsById } from "./sideLoadedDataSets";

export const getSideLoadedBusinessById =
  (recordId: string) => (state: unknown) =>
    getSideLoadedBusinesses(state)[recordId] || null;

export const selectSideLoadedBusinessesList = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses),
);

export interface GridSideLoadedBusiness extends SideLoadedBusiness {
  datasetLabel: string;
}

// Carried on the row rather than resolved in a valueGetter: the grid groups on
// this value, and a change to it has to reach the grid as a row data change.
export const selectSideLoadedBusinessesGridList = createSelector(
  [selectSideLoadedBusinessesList, selectSideLoadedDataSetLabelsById],
  (
    sideLoadedBusinesses: SideLoadedBusiness[],
    labelsById: Record<string, string>,
  ): GridSideLoadedBusiness[] =>
    sideLoadedBusinesses.map(sideLoadedBusiness => ({
      ...sideLoadedBusiness,
      // Falls back to the name until the data sets have loaded.
      datasetLabel:
        labelsById[sideLoadedBusiness.datasetId] ||
        sideLoadedBusiness.datasetName,
    })),
);

export const getSideLoadedBusinessesCount = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses).length,
);

export interface SearchFormattedSideLoadedBusiness extends SideLoadedBusiness {
  formattedName: string;
}

// Fuse options for searching side loaded businesses.
// These should be pretty good for finding a sweet spot of results.
// One common idiom it needs to handle is finding "Kevin Turner"
// when searching for any of "Turner, K", "Turner Associates", "Kevin T", etc - by tokeninzing down to
// simplified text, we should be able to get some results for all of these, without too much noise.
// ref: https://www.fusejs.io/api/options.html
const FUSE_OPTIONS: Fuse.IFuseOptions<SearchFormattedSideLoadedBusiness> = {
  keys: [
    { name: "name", weight: 2 },
    { name: "formattedName", weight: 3 },
    { name: "abn", weight: 1 },
  ],
  includeScore: true,
  isCaseSensitive: false,
  minMatchCharLength: 2, // ignore single character matches
  shouldSort: true,
  useExtendedSearch: true, // for tokenizing the search query and joining by "|" (OR)
  findAllMatches: true, // better rank multi token matches
  threshold: 0.2,
  // for matching primarily on names, the location of where the match occurs has little
  // to do with the ranking of the match, so lets ignore it.
  // e.g.
  //   - "Kevin Turner & John Carter of Mars"
  //   - "John Carter of Mars & Kevin Turner"
  // both should be a good match for Kevin
  // https://www.fusejs.io/concepts/scoring-theory.html#distance-threshold-and-location
  ignoreLocation: true,
  // complicated by using tokenized extended search, because we break up the search query to tokens,
  // the standard Field-length Norm setting means that we would always favor the shorter indexes,
  // https://www.fusejs.io/concepts/scoring-theory.html#field-length-norm
  ignoreFieldNorm: true,
};

// Tokenize the search name field into a format that Fuse can easily search.
// - strip punctuation
// - convert whitespace to single space
function formatSearchNameField(name: string): string {
  return name
    .replace(/[^\w\s]/g, " ")
    .trim()
    .split(/\s+/)
    .join(" ");
}

const IGNORED_SEARCH_TOKENS = ["and", "co", "inc", "ltd", "pty", "the"];

// Tokenize the search query into a format that Fuse can understand - basically
// - strip punctuation
// - split on space
// - ignore single character tokens
// - use include match with short tokens
function formatSearchQueryTokens(query: string): string[] {
  return query
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(n => n.length >= 2)
    .filter(n => !IGNORED_SEARCH_TOKENS.includes(n.toLowerCase()))
    .map(term => (term.length <= 3 ? `'${term}` : term));
}

export const selectSideLoadedBusinessSearchIndex = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) => {
    const searchFormattedBusinesses = Object.values(sideLoadedBusinesses).map(
      slb =>
        ({
          ...slb,
          formattedName: formatSearchNameField(slb.name),
        }) as SearchFormattedSideLoadedBusiness,
    );
    return new Fuse(searchFormattedBusinesses, FUSE_OPTIONS);
  },
);

export const searchSideLoadedBusinesses =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): Fuse.FuseResult<SearchFormattedSideLoadedBusiness>[] => {
    const formattedQueryTokens = formatSearchQueryTokens(query);
    if (!formattedQueryTokens.length) {
      return EMPTY_ARRAY;
    }

    const index = selectSideLoadedBusinessSearchIndex(state);

    const results = strict
      ? // strict search - get matches for all supplied tokens (AND = " ")
        index.search(formattedQueryTokens.join(" "))
      : // non-strict search - match any supplied tokens (OR = "|")
        index.search(formattedQueryTokens.join("|"));

    // Filtered on the results rather than the index so the one index still
    // serves the suggestion count and the grid's show-ignored view.
    if (includeIgnored) {
      return results;
    }
    return results.filter(result => !result.item.isIgnored);
  };

export interface SideLoadedBusinessWithMatches extends SideLoadedBusiness {
  matchedFields: SideLoadedMatchedField[];
}

// PIC/ABN/email are identifiers, not names - a one-character difference is a different
// property/business, not a typo. Match these with a normalized substring check rather than
// Fuse's fuzzy (Levenshtein-style) matching, mirroring the same normalization the backend
// applies when storing these fields.
const normalizePicOrAbn = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const normalizeDigits = (value: string): string => value.replace(/\D/g, "");

const normalizeLower = (value: string): string => value.trim().toLowerCase();

export function matchSideLoadedBusinessIdentifiers(
  businesses: SideLoadedBusiness[],
  query: string,
): SideLoadedBusinessWithMatches[] {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 2) {
    return EMPTY_ARRAY;
  }

  const picQuery = normalizePicOrAbn(trimmedQuery);
  const lowerQuery = normalizeLower(trimmedQuery);

  // Only treat the query as an ABN search when it's actually numeric - otherwise a
  // mostly-alpha query (e.g. "alai0") normalizes down to a trivial digit fragment that
  // would coincidentally substring-match almost any ABN.
  const isNumericQuery = /^\d+$/.test(trimmedQuery.replace(/\s/g, ""));
  const digitsQuery = isNumericQuery ? normalizeDigits(trimmedQuery) : "";

  return businesses.reduce(
    (matches: SideLoadedBusinessWithMatches[], business) => {
      const matchedFields: SideLoadedMatchedField[] = [];

      if (
        digitsQuery &&
        business.abn &&
        normalizeDigits(business.abn).includes(digitsQuery)
      ) {
        matchedFields.push("abn");
      }

      if (
        picQuery &&
        business.pics.split(PIC_DELIMITER).some(pic => pic.includes(picQuery))
      ) {
        matchedFields.push("pic");
      }

      if (
        business.brands &&
        normalizeLower(business.brands).includes(lowerQuery)
      ) {
        matchedFields.push("brands");
      }

      if (
        business.email &&
        normalizeLower(business.email).includes(lowerQuery)
      ) {
        matchedFields.push("email");
      }

      if (matchedFields.length) {
        matches.push({ ...business, matchedFields });
      }
      return matches;
    },
    [],
  );
}

export const searchSideLoadedBusinessesWithMatches =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): SideLoadedBusinessWithMatches[] => {
    const businesses = getSideLoadedBusinesses(state);
    const identifierMatches = matchSideLoadedBusinessIdentifiers(
      Object.values(businesses),
      query,
    ).filter(match => includeIgnored || !match.isIgnored);
    const identifierMatchIds = new Set(
      identifierMatches.map(match => match.id),
    );

    const fuzzyMatches = searchSideLoadedBusinesses(
      query,
      strict,
      includeIgnored,
    )(state)
      .filter(result => !identifierMatchIds.has(result.item.id))
      .map(result => ({ ...result.item, matchedFields: EMPTY_ARRAY }));

    return [...identifierMatches, ...fuzzyMatches];
  };

// The strict/liberal choice must be made on what's actually visible -
// otherwise a strict match that's only a currently-hidden ignored record
// starves the liberal fallback of a less-exact, non-ignored match it might
// have surfaced.
export function pickSideLoadedSearchResults(
  strictResults: SideLoadedBusinessWithMatches[],
  liberalResults: SideLoadedBusinessWithMatches[],
  showIgnored: boolean,
): SideLoadedBusinessWithMatches[] {
  const visibleStrictResults = strictResults.filter(
    result => showIgnored || !result.isIgnored,
  );
  return visibleStrictResults.length ? strictResults : liberalResults;
}

// Counts new suggestions only - used records are left in the search results
// for the grid, which labels them, but they are not work waiting to be done.
export const newSideLoadedBusinessSuggestionsCount =
  (query: string) =>
  (state: State): number =>
    searchSideLoadedBusinessesWithMatches(
      query,
      true,
    )(state).filter(result => !result.businessIds.length).length;

export const selectSideLoadedBusinessMappableFieldsByIdLookup = createSelector(
  [getSideLoadedBusinesses],
  sideLoadedBusinesses => {
    // Optimization here - all the mappable fields are the same for all side loaded businesses, so we can just get them once, then inject that
    // into the lookup
    const firstBusiness = Object.values(
      sideLoadedBusinesses,
    )?.[0] as SideLoadedBusiness;
    if (!firstBusiness) {
      return {};
    }

    const mappableFields = Object.keys(firstBusiness.rawData).sort((a, b) => {
      // Shunt name and abn to the top of the list, but keep everythign else in order.
      // TODO - be great to keep this the actual CSV order...
      if (a === "name" || a === "abn") return -1;
      if (b === "name" || b === "abn") return 1;
      return 0;
    });

    return Object.keys(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusinessId) => {
        acc[sideLoadedBusinessId] = mappableFields;
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableFieldsById =
  (recordId: string) => (state: State) =>
    selectSideLoadedBusinessMappableFieldsByIdLookup(state)[recordId] ||
    EMPTY_ARRAY;

const selectSideLoadedBusinessMappableOptionsByIdLookup = createSelector(
  [getSideLoadedBusinesses, selectSideLoadedBusinessMappableFieldsByIdLookup],
  (sideLoadedBusinesses, mappableFieldsByIdLookup) => {
    return Object.values(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusiness: SideLoadedBusiness) => {
        const mappableFields = mappableFieldsByIdLookup[sideLoadedBusiness.id];
        // Show only mappable fields with data.
        acc[sideLoadedBusiness.id] = mappableFields
          .filter(field => Boolean(sideLoadedBusiness?.rawData[field]))
          .reduce((rowAcc, field) => {
            const fieldValue = sideLoadedBusiness?.rawData[field];

            rowAcc.push({
              label: fieldValue,
              value: field,
              disabled: !fieldValue,
            });

            return rowAcc;
          }, []);
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableOptionsById =
  (recordId: string) => (state: State) => {
    return (
      selectSideLoadedBusinessMappableOptionsByIdLookup(state)[recordId] ||
      EMPTY_ARRAY
    );
  };
